import { FC, useState } from 'react';
import ReusableModal from '../common/Modal';
import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { commonStyles } from '../../utils/styles/inputs';
import { ICreateOrganization } from '../../schemas/apis/interface';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../hooks/useAlert';
import { createOrganizationApi } from '../../utils/apis';
import MessagePopup from '../common/MessagePopup';

interface CreateOrganisationModalProps {
  open: boolean;
  onClose: () => void;
}

const CreateOrganisationModal: FC<CreateOrganisationModalProps> = ({
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();
  const [organisationDetails, setOrganisationDetails] =
    useState<ICreateOrganization>({
      organization_name: '',
      organization_email: '',
    });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOrganisationDetails(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const {
    mutateAsync: createOrganisation,
    isPending: isCreateOrganisationPending,
  } = useMutation({
    mutationFn: createOrganizationApi,
    onSuccess: () => {
      showAlert('Organization created successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['get-organization'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['restaurants'] });
      onClose();
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to create organization',
        'error'
      );
    },
  });

  const handleCreateOrganisation = async () => {
    await createOrganisation(organisationDetails);
  };

  return (
    <>
      <ReusableModal open={open} onClose={onClose} title="Create Organization">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>
              Enter Organization Name
            </FormLabel>
            <TextField
              id="organisation-name"
              name="organization_name"
              placeholder="Organization Name"
              required
              fullWidth
              variant="outlined"
              sx={commonStyles.textField}
              value={organisationDetails.organization_name}
              onChange={handleChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <FormLabel sx={commonStyles.formLabel}>
              Enter Organization Email
            </FormLabel>
            <TextField
              id="organisation-email"
              name="organization_email"
              placeholder="Organization Email"
              required
              fullWidth
              variant="outlined"
              sx={commonStyles.textField}
              value={organisationDetails.organization_email}
              onChange={handleChange}
            />
          </FormControl>

          <Button
            variant="contained"
            sx={{ width: '100%', textTransform: 'none' }}
            disabled={
              (!organisationDetails.organization_name &&
                !organisationDetails.organization_email) ||
              isCreateOrganisationPending
            }
            onClick={() => void handleCreateOrganisation()}
          >
            {isCreateOrganisationPending
              ? 'Creating...'
              : 'Create Organization'}
          </Button>
        </Box>
      </ReusableModal>

      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default CreateOrganisationModal;
